import React, { useEffect, useState } from 'react'
import { getInitialLang } from '../Common'
import { $MAX_WIDTH } from '../Consts'

type Lang = 'en' | 'zh'

const contactEndpoint = 'https://contact.serverless.businessa.com'

const EN = {
  eyebrow: 'Contact Us',
  h1: 'How can we help?',
  p: 'Tell us what you need and our team will get back to you shortly.',
  submit: 'Submit',
  sending: 'Sending...',
  error: 'Oops – something went wrong. Please try again.',
  successTitle: "Thanks, we've received your message",
  successText:
    'Our team will review your question and get back to you as soon as possible.',
  labels: {
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    query: 'Your question or message',
  },
  placeholders: {
    name: 'Jane Doe',
    email: 'jane@company.com',
    phone: '+1 555-123-4567',
    query: 'Tell us how we can help...',
  },
}

const ZH = {
  eyebrow: '联系我们',
  h1: '我们能如何帮助您？',
  p: '请告诉我们您的需求，我们的团队会尽快与您联系。',
  submit: '提交',
  sending: '提交中...',
  error: '抱歉，发生错误，请重试。',
  successTitle: '谢谢，我们已收到您的留言',
  successText: '我们的团队会查看您的问题，并尽快与您联系。',
  labels: {
    name: '姓名',
    email: '邮箱',
    phone: '电话',
    query: '您的问题或留言',
  },
  placeholders: {
    name: '张三',
    email: 'zhangsan@company.com',
    phone: '+86 138-0000-0000',
    query: '请告诉我们您需要什么帮助…',
  },
}

const LANGS = { en: EN, zh: ZH }

const createInitialForm = () => ({
  name: '',
  email: '',
  phone: '',
  query: '',
})

const useDesktop = () => {
  const [desktop, setDesktop] = useState(
    () =>
      typeof window === 'undefined' ||
      window.matchMedia('(min-width:1024px)').matches
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    const media = window.matchMedia('(min-width:1024px)')
    const update = () => setDesktop(media.matches)

    update()

    if (media.addEventListener) {
      media.addEventListener('change', update)
      return () => media.removeEventListener('change', update)
    }

    media.addListener(update)
    return () => media.removeListener(update)
  }, [])

  return desktop
}

const ContactPage: React.FC = () => {
  const desktop = useDesktop()
  const [lang] = useState<Lang>(getInitialLang)
  const [submitted, setSubmitted] = useState(false)
  const [status, setStatus] = useState<
    'idle' | 'sending' | 'success' | 'error'
  >('idle')
  const [buttonHover, setButtonHover] = useState(false)
  const [form, setForm] = useState(createInitialForm)

  const t = LANGS[lang]

  const grid =
    'repeating-linear-gradient(to right,rgba(255,255,255,.065) 0 1px,transparent 1px 80px),repeating-linear-gradient(to bottom,rgba(255,255,255,.065) 0 1px,transparent 1px 80px)'

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: desktop ? '1.2rem 1.25rem' : '1rem 1rem',
    borderRadius: 18,
    border: '1px solid rgba(255,255,255,.28)',
    background: 'rgba(255,255,255,.14)',
    color: '#fff',
    outline: 'none',
    fontSize: desktop ? '1.05rem' : '1rem',
    fontFamily: 'inherit',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.1)',
  }

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '.7rem',
    fontSize: '1rem',
    fontWeight: 800,
    color: 'rgba(255,255,255,.94)',
  }

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target
    setForm((current) => ({ ...current, [name]: value }))

    if (status === 'error') {
      setStatus('idle')
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (status === 'sending') return

    setStatus('sending')

    try {
      const res = await fetch(contactEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })

      if (!res.ok) throw new Error('Network error')

      setStatus('success')
      setSubmitted(true)
      setForm(createInitialForm())

      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    } catch {
      setStatus('error')
    }
  }

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100vh',
        boxSizing: 'border-box',
        background:
          'linear-gradient(180deg,#4fa6e5 0%,#3289dc 34%,#266bc8 68%,#102b70 100%)',
        color: '#fff',
        fontFamily:
          'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>
        {`
          .contact-control::placeholder {
            color: rgba(255,255,255,.92);
            opacity: 1;
          }

          .contact-control::-ms-input-placeholder {
            color: rgba(255,255,255,.92);
          }

          .contact-control:focus {
            border-color: rgba(255,255,255,.62);
            box-shadow: inset 0 1px 0 rgba(255,255,255,.12), 0 0 0 4px rgba(255,255,255,.1);
          }
        `}
      </style>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: grid,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'absolute',
          width: 620,
          height: 620,
          borderRadius: '50%',
          background:
            'radial-gradient(circle,rgba(118,206,255,.42),rgba(118,206,255,0) 68%)',
          top: -220,
          right: -190,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'absolute',
          width: 640,
          height: 640,
          borderRadius: '50%',
          background:
            'radial-gradient(circle,rgba(74,112,255,.32),rgba(74,112,255,0) 70%)',
          bottom: -260,
          left: -230,
          pointerEvents: 'none',
        }}
      />

      <section
        style={{
          maxWidth: $MAX_WIDTH,
          width: '100%',
          margin: '0 auto',
          padding: desktop ? '7rem 2rem 6rem' : '5rem 1.25rem 4rem',
          boxSizing: 'border-box',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <header
          style={{
            maxWidth: 980,
            margin: '0 auto',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: desktop ? '1rem' : '.92rem',
              fontWeight: 850,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,.82)',
              marginBottom: '1rem',
            }}
          >
            {t.eyebrow}
          </div>

          <h1
            style={{
              fontSize: desktop ? '4.75rem' : '2.65rem',
              fontWeight: 900,
              lineHeight: 1.02,
              letterSpacing: '-.06em',
              margin: 0,
            }}
          >
            {t.h1}
          </h1>

          <p
            style={{
              fontSize: desktop ? '1.22rem' : '1.04rem',
              opacity: 0.92,
              maxWidth: 790,
              margin: '1.45rem auto 0',
              lineHeight: 1.75,
            }}
          >
            {t.p}
          </p>
        </header>

        <div
          style={{
            width: '100%',
            maxWidth: 1120,
            margin: desktop ? '4rem auto 0' : '2.75rem auto 0',
            borderRadius: desktop ? 38 : 28,
            padding: desktop ? '1.55rem' : '.9rem',
            background:
              'linear-gradient(180deg,rgba(255,255,255,.2),rgba(255,255,255,.08))',
            border: '1px solid rgba(255,255,255,.28)',
            boxShadow: '0 34px 90px rgba(0,0,0,.24)',
            backdropFilter: 'blur(22px)',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              borderRadius: desktop ? 30 : 22,
              padding: desktop ? '3rem' : '1.35rem',
              background: 'rgba(7,24,68,.42)',
              border: '1px solid rgba(255,255,255,.16)',
              boxSizing: 'border-box',
            }}
          >
            {submitted ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: desktop ? '3.85rem 1rem' : '2.5rem .5rem',
                  maxWidth: 720,
                  margin: '0 auto',
                }}
              >
                <div
                  style={{
                    width: desktop ? 96 : 84,
                    height: desktop ? 96 : 84,
                    borderRadius: desktop ? 30 : 26,
                    background:
                      'linear-gradient(135deg,rgba(255,255,255,.24),rgba(255,255,255,.08))',
                    border: '1px solid rgba(255,255,255,.3)',
                    boxShadow:
                      '0 24px 60px rgba(0,0,0,.24), inset 0 1px 0 rgba(255,255,255,.22)',
                    margin: '0 auto 1.7rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg
                    width={desktop ? 54 : 48}
                    height={desktop ? 54 : 48}
                    viewBox="0 0 56 56"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M15 29.5L24.2 38.5L42 18.5"
                      stroke="url(#contactSuccessCheck)"
                      strokeWidth="5.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <defs>
                      <linearGradient
                        id="contactSuccessCheck"
                        x1="15"
                        y1="18.5"
                        x2="42"
                        y2="38.5"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#8fffea" />
                        <stop offset="1" stopColor="#ffffff" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                <h2
                  style={{
                    margin: 0,
                    fontSize: desktop ? '2.55rem' : '1.78rem',
                    letterSpacing: '-.045em',
                    lineHeight: 1.1,
                  }}
                >
                  {t.successTitle}
                </h2>

                <p
                  style={{
                    margin: '1.15rem auto 0',
                    maxWidth: 620,
                    fontSize: desktop ? '1.08rem' : '1rem',
                    lineHeight: 1.75,
                    opacity: 0.88,
                  }}
                >
                  {t.successText}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: desktop ? '1fr 1fr' : '1fr',
                    gap: desktop ? '1.55rem 1.75rem' : '1.1rem',
                  }}
                >
                  <label style={labelStyle}>
                    {t.labels.name}
                    <input
                      className="contact-control"
                      name="name"
                      required
                      type="text"
                      value={form.name}
                      onChange={handleChange}
                      placeholder={t.placeholders.name}
                      style={inputStyle}
                      autoComplete="off"
                    />
                  </label>

                  <label style={labelStyle}>
                    {t.labels.email}
                    <input
                      className="contact-control"
                      name="email"
                      required
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder={t.placeholders.email}
                      style={inputStyle}
                      autoComplete="off"
                    />
                  </label>
                </div>

                <label
                  style={{
                    ...labelStyle,
                    marginTop: desktop ? '1.75rem' : '1.15rem',
                  }}
                >
                  {t.labels.phone}
                  <input
                    className="contact-control"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder={t.placeholders.phone}
                    style={inputStyle}
                    autoComplete="off"
                  />
                </label>

                <label
                  style={{
                    ...labelStyle,
                    marginTop: desktop ? '1.75rem' : '1.15rem',
                  }}
                >
                  {t.labels.query}
                  <textarea
                    className="contact-control"
                    name="query"
                    required
                    value={form.query}
                    onChange={handleChange}
                    placeholder={t.placeholders.query}
                    style={{
                      ...inputStyle,
                      minHeight: desktop ? 190 : 160,
                      resize: 'vertical',
                      lineHeight: 1.65,
                    }}
                    autoComplete="off"
                  />
                </label>

                <button
                  type="submit"
                  style={{
                    width: '100%',
                    marginTop: desktop ? '1.9rem' : '1.35rem',
                    background: buttonHover
                      ? 'linear-gradient(90deg,#ffffff 0%,#dcecff 100%)'
                      : 'linear-gradient(90deg,#ffffff 0%,#eef6ff 100%)',
                    color: '#123578',
                    padding: desktop ? '1.25rem 2rem' : '1.08rem 2rem',
                    fontSize: desktop ? '1.1rem' : '1.04rem',
                    fontWeight: 900,
                    border: 'none',
                    borderRadius: 18,
                    cursor: status === 'sending' ? 'not-allowed' : 'pointer',
                    opacity: status === 'sending' ? 0.68 : 1,
                    boxShadow: buttonHover
                      ? '0 20px 42px rgba(0,0,0,.23)'
                      : '0 14px 32px rgba(0,0,0,.17)',
                    transition:
                      'box-shadow .25s, transform .25s, background .25s',
                    transform: buttonHover ? 'translateY(-2px)' : 'translateY(0)',
                    fontFamily: 'inherit',
                  }}
                  onMouseEnter={() => setButtonHover(true)}
                  onMouseLeave={() => setButtonHover(false)}
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? t.sending : t.submit}
                </button>

                {status === 'error' && (
                  <p
                    style={{
                      margin: '1rem 0 0',
                      color: '#ffd8d8',
                      fontSize: desktop ? '1rem' : '.95rem',
                      fontWeight: 800,
                      textAlign: 'center',
                    }}
                  >
                    {t.error}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

export default ContactPage